import { h, } from "@stencil/core";
import { createLogger } from "@eventstore-ui/utils";
import { iconStore } from "../../utils/iconStore";
const logger = createLogger('es-icon', 'orange');
/**
 * Displays an icon loaded from the `iconStore`. An icon named "spinner" will automatically spin.
 * See [IconStore](/components/variables/iconStore) for details on how to load icons.
 * @part icon - The internal icon.
 */
export class Icon {
    constructor() {
        this.parseIconDescription = (description) => {
            if (typeof description === 'string') {
                return [undefined, description];
            }
            return description;
        };
        this.loadIcon = async (description) => {
            if (!description)
                return;
            const [namespace, name] = this.parseIconDescription(description);
            this.autoSpin = name === 'spinner';
            if (!iconStore.has(name, namespace)) {
                logger.log.once(`Unknown icon: '${name}'${namespace ? ` in namespace ${String(namespace)}` : ''}.`, 'Please add it to iconStore.');
                return;
            }
            const component = await iconStore.get(name, namespace);
            this.Component = component(h);
        };
        this.icon = undefined;
        this.size = 24;
        this.angle = 0;
        this.spin = undefined;
        this.spinDirection = 'clockwise';
        this.autoSpin = false;
        this.Component = (props) => (h("svg", { ...props }));
    }
    /** Provides a promise that resolves at the end of a single spin, if the icon is spinning. */
    async spinEnd() {
        const spinner = this.host.shadowRoot?.querySelector('.spin');
        if (!spinner)
            return;
        return new Promise((resolve) => {
            const spinComplete = () => {
                spinner.removeEventListener('animationiteration', spinComplete);
                resolve();
            };
            spinner.addEventListener('animationiteration', spinComplete);
        });
    }
    componentWillLoad() {
        this.loadIcon(this.icon);
    }
    onIconChange(icon, previousIcon) {
        if (icon !== previousIcon) {
            this.loadIcon(this.icon);
        }
    }
    render() {
        const Component = this.Component;
        return (h(Component, { key: '91eb58b2f19972746854e3d7c3a53e2625c165bf', part: 'icon', "aria-hidden": 'true', focusable: 'false', role: 'img', height: this.size, width: this.size, class: {
                spin: this.spin || (this.spin == null && this.autoSpin),
                antiClockwise: this.spinDirection === 'antiClockwise',
            }, style: this.angle !== 0
                ? { transform: `rotate(${this.angle}deg)` }
                : undefined }));
    }
    static get is() { return "es-icon"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["es-icon.css"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["es-icon.css"]
        };
    }
    static get properties() {
        return {
            "icon": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "IconDescription",
                    "resolved": "[namespace: string | symbol, name: string] | string",
                    "references": {
                        "IconDescription": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/es-icon/types.ts::IconDescription"
                        }
                    }
                },
                "required": true,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Which icon to display."
                },
                "attribute": "icon",
                "reflect": false
            },
            "size": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The hight and width to scale the icon to."
                },
                "attribute": "size",
                "reflect": false,
                "defaultValue": "24"
            },
            "angle": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Rotate the icon to a speciied angle."
                },
                "attribute": "angle",
                "reflect": false,
                "defaultValue": "0"
            },
            "spin": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Apply a spin animation."
                },
                "attribute": "spin",
                "reflect": false
            },
            "spinDirection": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "'clockwise' | 'antiClockwise'",
                    "resolved": "\"antiClockwise\" | \"clockwise\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "When spinning, should it spin clockwise or anticlockwise."
                },
                "attribute": "spin-direction",
                "reflect": false,
                "defaultValue": "'clockwise'"
            }
        };
    }
    static get states() {
        return {
            "autoSpin": {},
            "Component": {}
        };
    }
    static get methods() {
        return {
            "spinEnd": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Provides a promise that resolves at the end of a single spin, if the icon is spinning.",
                    "tags": []
                }
            }
        };
    }
    static get elementRef() { return "host"; }
    static get watchers() {
        return [{
                "propName": "icon",
                "methodName": "onIconChange"
            }];
    }
}
//# sourceMappingURL=es-icon.js.map
